import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, ArrowDropDown } from "@mui/icons-material";
import { tokens } from "../../../theme";
import Button from "../../../components/common/Button";
import { useState, useEffect } from "react";
import { MemoryBookEntry } from "./MemoryBookCard";

type MemoryBookStatus = "overdue" | "in_danger" | "safe";
type AssignmentRole = "scanner" | "uploader" | "both" | "other" | null;

const ROLE_OPTIONS: { value: Exclude<AssignmentRole, null>; label: string }[] = [
  { value: "scanner", label: "Physical Comments Scanner" },
  { value: "uploader", label: "Online Comments Uploader" },
  { value: "both", label: "Both" },
  { value: "other", label: "Other" },
];

const ROLE_LABELS: Record<Exclude<AssignmentRole, null>, string> = {
  scanner: "Scanner",
  uploader: "Uploader",
  both: "Both",
  other: "Other",
};

interface StaffAssignment {
  id: number;
  avatarUrl?: string;
  name: string;
  assignedCount: number;
}

interface MemoryBookReassignModalProps {
  open: boolean;
  onClose: () => void;
  entry: MemoryBookEntry | null;
  staffOptions: StaffAssignment[];
  onAssign: (streamId: number, staffId: number) => void;
  onUnassign: (assignmentId: number) => void;
  onChangeRole: (assignmentId: number, role: AssignmentRole) => void;
  onViewBook: () => void;
}

const STATUS_META: Record<
  MemoryBookStatus,
  { label: string; color: string; bg: string }
> = {
  overdue: { label: "This book is overdue", color: "#E11D48", bg: "#FAD7DF" },
  in_danger: { label: "This book is in danger", color: "#f59e0b", bg: "#FEF0C7" },
  safe: { label: "This book is safe", color: "#10b981", bg: "#D1FAE5" },
};

const MemoryBookReassignModal = ({
  open,
  onClose,
  entry,
  staffOptions,
  onAssign,
  onUnassign,
  onChangeRole,
  onViewBook,
}: MemoryBookReassignModalProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [localAssignees, setLocalAssignees] = useState(entry?.assignees ?? []);

  const [isDragging, setIsDragging] = useState(false);
  const [dragOverAssigneeZone, setDragOverAssigneeZone] = useState(false);
  const [dragOverStaffZone, setDragOverStaffZone] = useState(false);
  const [draggedStaffId, setDraggedStaffId] = useState<number | null>(null);
  const [draggedAssignmentId, setDraggedAssignmentId] = useState<number | null>(null);
  const [roleMenu, setRoleMenu] = useState<{
    anchorEl: HTMLElement;
    assignmentId: number;
  } | null>(null);

  // Sync local state whenever entry changes (e.g. modal reopens with fresh data)
  useEffect(() => {
    if (entry) setLocalAssignees(entry.assignees);
  }, [entry]);

  if (!entry) return null;

  const meta = STATUS_META[entry.status];
  const assignedIds = new Set(localAssignees.map((a) => a.id));
  const availableStaff = staffOptions.filter((s) => !assignedIds.has(s.id));

  const isDaysAgo = entry.timeInfo.toLowerCase().includes("days ago");
  const daysMatch = entry.timeInfo.match(/(\d+)/);
  const daysPassed = daysMatch ? parseInt(daysMatch[1]) : null;

  const handleAssign = (streamId: number, staffId: number) => {
    const staff = staffOptions.find((s) => s.id === staffId);
    if (!staff) return;

    // Optimistically add to local assignees with a temp assignmentId
    setLocalAssignees((prev) => [
      ...prev,
      {
        id: staff.id,
        assignmentId: -Date.now(), // temp ID until parent refreshes
        name: staff.name,
        avatarUrl: staff.avatarUrl,
      },
    ]);

    onAssign(streamId, staffId);
  };

  const handleUnassign = (assignmentId: number) => {
    // Optimistically remove from local assignees
    setLocalAssignees((prev) =>
      prev.filter((a) => a.assignmentId !== assignmentId)
    );

    onUnassign(assignmentId);
  };

  const handleSelectRole = (assignmentId: number, role: AssignmentRole) => {
    setLocalAssignees((prev) =>
      prev.map((a) => (a.assignmentId === assignmentId ? { ...a, role } : a)),
    );
    onChangeRole(assignmentId, role);
    setRoleMenu(null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "30px",
          backgroundColor: colors.primary[800],
          backgroundImage: "none",
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          pt: 2,
          pb: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.12em",
            color: colors.primary[700],
            textTransform: "uppercase",
          }}
        >
          Manage Assignees
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: colors.primary[700] }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 2.5, pb: 2.5, pt: 1.5 }}>
        {/* Deceased info card */}
        <Box sx={{ position: "relative", mb: 2.5, pt: 1.8 }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 28,
              zIndex: 2,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.8,
              px: 1.8,
              py: 0.7,
              borderRadius: "50px",
              backgroundColor: meta.bg,
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: meta.color,
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: meta.color }}>
              {meta.label}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              pt: 3,
              borderRadius: "20px",
              border: `1px solid ${meta.color}40`,
              backgroundColor: colors.primary[900],
            }}
          >
            <Avatar
              src={entry.deceasedAvatarUrl}
              sx={{ width: 72, height: 72, flexShrink: 0, borderRadius: "50px" }}
            >
              {entry.deceasedName[0]}
            </Avatar>
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.5rem",
                  color: colors.primary[700],
                  lineHeight: 1.2,
                  mb: 0.5,
                }}
              >
                {entry.deceasedName}
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: colors.primary[100], mb: 0.2 }}>
                Due Date:{" "}
                <Box component="span" sx={{ color: colors.primary[700] }}>
                  {entry.timeInfo}
                </Box>
              </Typography>
              {isDaysAgo && daysPassed !== null && (
                <Typography sx={{ fontSize: "0.8rem", color: colors.primary[100] }}>
                  Days passed:{" "}
                  <Box component="span" sx={{ color: colors.primary[700] }}>
                    {daysPassed} {daysPassed === 1 ? "day" : "days"}
                  </Box>
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Current Assignees — lights up with meta.color when dragging from staff */}
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.1em",
            color: colors.primary[700],
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          Current Assignees ({localAssignees.length})
        </Typography>
        <Box
          onDragOver={(e) => {
            e.preventDefault();
            if (draggedStaffId) setDragOverAssigneeZone(true);
          }}
          onDragLeave={() => setDragOverAssigneeZone(false)}
          onDrop={() => {
            if (draggedStaffId) handleAssign(entry.id, draggedStaffId);
            setDragOverAssigneeZone(false);
            setDraggedStaffId(null);
            setIsDragging(false);
          }}
          sx={{
            position: "relative",
            borderRadius: "16px",
            backgroundColor: colors.primary[900],
            mb: 2.5,
            border: dragOverAssigneeZone
              ? `2px dashed ${meta.color}`
              : `2px solid transparent`,
            transition: "border 0.2s ease",
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              width: 32,
              pointerEvents: "none",
              background: `linear-gradient(to right, transparent, ${colors.primary[900]})`,
              borderRadius: "0 16px 16px 0",
              opacity: localAssignees.length > 4 ? 1 : 0,
              transition: "opacity 0.2s ease",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              overflowX: "auto",
              gap: 1.5,
              p: 1.5,
              minHeight: 76,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {localAssignees.length === 0 ? (
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: colors.primary[700],
                  alignSelf: "center",
                  px: 1,
                }}
              >
                No one assigned — drag a staff member here
              </Typography>
            ) : (
              localAssignees.map((assignee) => (
                <Tooltip key={assignee.assignmentId} title={assignee.name} arrow>
                  <Box
                    draggable
                    onDragStart={() => {
                      setIsDragging(true);
                      setDraggedAssignmentId(assignee.assignmentId);
                    }}
                    onDragEnd={() => {
                      setIsDragging(false);
                      setDraggedAssignmentId(null);
                      setDragOverStaffZone(false);
                    }}
                    sx={{
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      pr: 1.5,
                      borderRadius: "50px",
                      backgroundColor: colors.primary[800],
                      cursor: "grab",
                      opacity: draggedAssignmentId === assignee.assignmentId ? 0.4 : 1,
                      "&:active": { cursor: "grabbing" },
                    }}
                  >
                    <Avatar src={assignee.avatarUrl} sx={{ width: 32, height: 32 }}>
                      {assignee.name[0]}
                    </Avatar>
                    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.82rem",
                          color: colors.primary[100],
                          whiteSpace: "nowrap",
                        }}
                      >
                        {assignee.name}
                      </Typography>
                      <Box
                        draggable={false}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          setRoleMenu({
                            anchorEl: e.currentTarget,
                            assignmentId: assignee.assignmentId,
                          });
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          width: "fit-content",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            color: assignee.role ? meta.color : colors.primary[700],
                            whiteSpace: "nowrap",
                          }}
                        >
                          {assignee.role ? ROLE_LABELS[assignee.role] : "Set role"}
                        </Typography>
                        <ArrowDropDown
                          sx={{ fontSize: 14, color: colors.primary[700] }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Tooltip>
              ))
            )}
          </Box>
        </Box>

        <Menu
          anchorEl={roleMenu?.anchorEl ?? null}
          open={!!roleMenu}
          onClose={() => setRoleMenu(null)}
        >
          {ROLE_OPTIONS.map((opt) => (
            <MenuItem
              key={opt.value}
              onClick={() =>
                roleMenu && handleSelectRole(roleMenu.assignmentId, opt.value)
              }
            >
              {opt.label}
            </MenuItem>
          ))}
          <MenuItem
            onClick={() => roleMenu && handleSelectRole(roleMenu.assignmentId, null)}
          >
            Clear role
          </MenuItem>
        </Menu>

        {/* Available Staff — lights up RED when dragging an assignee down */}
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.1em",
            color: colors.primary[700],
            textTransform: "uppercase",
            mb: 1.2,
          }}
        >
          Available Staff ({availableStaff.length})
        </Typography>
        <Box
          onDragOver={(e) => {
            e.preventDefault();
            if (draggedAssignmentId !== null) setDragOverStaffZone(true);
          }}
          onDragLeave={() => setDragOverStaffZone(false)}
          onDrop={() => {
            if (draggedAssignmentId !== null) handleUnassign(draggedAssignmentId);
            setDragOverStaffZone(false);
            setDraggedAssignmentId(null);
            setIsDragging(false);
          }}
          sx={{
            position: "relative",
            borderRadius: "16px",
            backgroundColor: colors.primary[900],
            mb: 3,
            border: dragOverStaffZone
              ? `2px dashed #E11D48`
              : `2px solid transparent`,
            transition: "border 0.2s ease",
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              width: 32,
              pointerEvents: "none",
              background: `linear-gradient(to right, transparent, ${colors.primary[900]})`,
              borderRadius: "0 16px 16px 0",
              opacity: availableStaff.length > 5 ? 1 : 0,
              transition: "opacity 0.2s ease",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexWrap: "nowrap",
              overflowX: "auto",
              p: 1.5,
              minHeight: 76,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {availableStaff.length === 0 ? (
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: colors.primary[700],
                  alignSelf: "center",
                  px: 1,
                }}
              >
                Everyone is already assigned
              </Typography>
            ) : (
              availableStaff.map((staff) => (
                <Tooltip key={staff.id} title={staff.name} arrow>
                  <Box
                    draggable
                    onDragStart={() => {
                      setIsDragging(true);
                      setDraggedStaffId(staff.id);
                    }}
                    onDragEnd={() => {
                      setIsDragging(false);
                      setDraggedStaffId(null);
                      setDragOverAssigneeZone(false);
                    }}
                    sx={{
                      flexShrink: 0,
                      cursor: "grab",
                      borderRadius: "50%",
                      border: `3px solid ${colors.primary[500] + "60"}`,
                      transition: "border-color 0.2s ease, transform 0.15s ease",
                      opacity: isDragging && draggedStaffId !== staff.id ? 0.5 : 1,
                      "&:active": { cursor: "grabbing" },
                      "&:hover": {
                        borderColor: meta.color,
                        transform: "scale(1.08)",
                      },
                    }}
                  >
                    <Avatar
                      src={staff.avatarUrl}
                      sx={{ width: 60, height: 60, border: `2px solid ${colors.primary[800]}` }}
                    >
                      {staff.name[0]}
                    </Avatar>
                  </Box>
                </Tooltip>
              ))
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={onViewBook}
            sx={{
              px: 3.5,
              py: 1.2,
              borderRadius: "12px",
              fontWeight: 700,
              fontSize: "0.85rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            View Book
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MemoryBookReassignModal;